import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import * as api from '../api';
import { useAuth } from '../auth/contexte-auth';
import {
  ContexteFile,
  type ApiDeconnexion,
  type ApiFile,
  type DialogueDeconnexion,
  type InstantaneFile,
  type ValeurFile,
} from './contexte-file';
import { obtenirDiffusion } from './diffusion';
import { maintenantServeur } from './horloge';
import { memoire } from './memoire';
import { MoteurEnvois, type DependancesMoteur, type EvenementMoteur } from './moteur';
import { MESSAGE_STOCKAGE_INDISPONIBLE, ouvrirStockage, type StockageFile } from './stockage';
import { transportApi } from './transport';
import type { CleSaisie, DemandeMiseEnFile, ResultatMiseEnFile } from './types';
import { verrouNavigateur } from './verrou';

/**
 * Fournisseur de la file d'envoi (docs/UI.md § Écran 3, mode hors ligne).
 * Monté sous `FournisseurAuth`, toujours, même sans session : il ouvre le
 * stockage, purge les saisies de plus de 7 jours et compte ce qui attend, pour
 * l'écran de connexion comme pour les bandeaux. Le moteur n'envoie que sous une
 * session connectée, et pas tant qu'un autre compte a des saisies sur le
 * téléphone sans que l'utilisateur ait choisi quoi en faire.
 */

/** Publié par `main.tsx` quand un nouveau service worker prend la main. */
export const EVENEMENT_MISE_A_JOUR = 'rh-app:mise-a-jour';

const PURGE_PERIODE_MS = 60 * 60 * 1000;
const TOUT_ENVOYE_MS = 6_000;
const ARRET_DECONNEXION_MS = 20_000;
const DELAI_SONDE_MS = 5_000;

export interface DependancesFile extends Partial<DependancesMoteur> {
  stockage?: StockageFile;
  /** Preuve du réseau au retour de `online` : une requête sans jeton ni effet. */
  sonder?: () => Promise<unknown>;
}

const INITIAL: InstantaneFile = {
  pret: false,
  stockage: 'indexeddb',
  enLigne: true,
  recensement: null,
  resumes: [],
  enAttente: 0,
  refusees: 0,
  envoiEnCours: null,
  prochainEssai: null,
  echecsConsecutifs: 0,
  enEchecDepuis: null,
  dernierePanne: null,
  dernierEnvoiLe: null,
  toutEnvoyeLe: null,
  effaceesPourAnciennete: 0,
  miseAJourRequise: false,
  miseAJourBloquee: false,
  autresComptes: 0,
  compteDesactive: false,
};

/** `navigator.onLine` n'est qu'un indice ; la dernière réponse (ou panne) de l'API tranche. */
function reseauProbable(): boolean {
  return globalThis.navigator?.onLine !== false;
}

function lireReseau(): boolean {
  return reseauProbable() && api.dernierEtatReseau() !== 'hors_ligne';
}

function ecouterEvenement(cible: EventTarget | undefined, type: string, action: () => void): () => void {
  if (!cible) return () => undefined;
  cible.addEventListener(type, action);
  return () => cible.removeEventListener(type, action);
}

/** Le moteur purge aussi avant chaque tournée : on compte ses purges comme les nôtres. */
function avecComptePurge(stockage: StockageFile, surPurge: (nombre: number) => void): StockageFile {
  return new Proxy(stockage, {
    get(cible, cle) {
      if (cle === 'purgerExpires') {
        return async (maintenant: number) => {
          const nombre = await cible.purgerExpires(maintenant);
          if (nombre > 0) surPurge(nombre);
          return nombre;
        };
      }
      const valeur: unknown = Reflect.get(cible, cle, cible);
      return typeof valeur === 'function' ? valeur.bind(cible) : valeur;
    },
  });
}

export function FournisseurFile({ enfants, dependances }: { enfants: ReactNode; dependances?: DependancesFile }) {
  const auth = useAuth();
  const [deps] = useState<DependancesFile>(() => dependances ?? {});
  const [diffusion] = useState(() => deps.diffusion ?? obtenirDiffusion());
  const horloge = deps.horloge ?? maintenantServeur;

  const [etat, setEtat] = useState<InstantaneFile>(() => ({ ...INITIAL, enLigne: lireReseau() }));
  const [stockage, setStockage] = useState<StockageFile | null>(null);
  const [moteur, setMoteur] = useState<MoteurEnvois | null>(null);
  const [autreCompteChoisi, setAutreCompteChoisi] = useState<string | null>(null);
  const [dialogue, setDialogue] = useState<DialogueDeconnexion | null>(null);

  const uid = auth.etat === 'connecte' ? (auth.utilisateur?.id ?? null) : null;
  const autres =
    uid && etat.recensement
      ? Object.entries(etat.recensement.parUtilisateur)
          .filter(([compte]) => compte !== uid)
          .reduce((total, [, nombre]) => total + nombre, 0)
      : 0;
  const autresComptes = uid && autreCompteChoisi !== uid ? autres : 0;
  const peutEnvoyer = moteur !== null && uid !== null && etat.recensement !== null && autresComptes === 0;

  // Lus par les rappels du moteur et des écouteurs, qui vivent plus longtemps qu'un rendu.
  const uidRef = useRef(uid);
  uidRef.current = uid;
  const peutEnvoyerRef = useRef(peutEnvoyer);
  peutEnvoyerRef.current = peutEnvoyer;
  const stockageRef = useRef<StockageFile | null>(null);
  const moteurRef = useRef<MoteurEnvois | null>(null);
  const ecouteurs = useRef(new Set<(evenement: EvenementMoteur) => void>());
  const sequence = useRef(0);
  const minuteurToutEnvoye = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Comptes désactivés (`COMPTE_INACTIF`) annoncés avant l'ouverture du stockage : purgés à l'ouverture. */
  const comptesDesactives = useRef(new Set<string>());

  const maj = useCallback((partiel: Partial<InstantaneFile> | ((i: InstantaneFile) => Partial<InstantaneFile>)) => {
    setEtat((i) => ({ ...i, ...(typeof partiel === 'function' ? partiel(i) : partiel) }));
  }, []);

  /** Recompte et relit les résumés ; seule la dernière lecture lancée s'applique. Rend le nombre en attente. */
  const rafraichir = useCallback(async (): Promise<number | null> => {
    const s = stockageRef.current;
    if (!s) return null;
    const numero = ++sequence.current;
    const compte = uidRef.current;
    try {
      const recensement = await s.recenser(horloge());
      const resumes = compte ? await s.resumes(compte) : [];
      if (numero !== sequence.current) return null;
      const enAttente = resumes.filter((r) => r.etat === 'en_attente').length;
      maj({
        pret: true,
        stockage: s.genre,
        recensement,
        resumes,
        enAttente,
        refusees: resumes.filter((r) => r.etat === 'refuse').length,
      });
      return enAttente;
    } catch (erreur) {
      console.error(erreur);
      return null;
    }
  }, [horloge, maj]);
  const rafraichirRef = useRef(rafraichir);
  rafraichirRef.current = rafraichir;

  // Ouverture du stockage, purge des 7 jours, création du moteur (arrêté tant qu'il n'y a pas de session).
  useEffect(() => {
    let vivant = true;
    let ouvert: StockageFile | null = null;
    let cree: MoteurEnvois | null = null;
    const ouverture = deps.stockage
      ? Promise.resolve(deps.stockage)
      : ouvrirStockage({
          surVersionChange: () => maj({ miseAJourRequise: true }),
          surBloque: () => {
            maj({ miseAJourBloquee: true });
            diffusion.publier({ type: 'mise-a-jour-requise' });
          },
          surBascule: () => void rafraichirRef.current(),
        });
    void ouverture.then(async (brut) => {
      if (!vivant) {
        if (!deps.stockage) brut.fermer();
        return;
      }
      const s = avecComptePurge(brut, (nombre) =>
        maj((i) => ({ effaceesPourAnciennete: i.effaceesPourAnciennete + nombre })),
      );
      ouvert = brut;
      cree = new MoteurEnvois({
        stockage: s,
        transport: deps.transport ?? transportApi(),
        verrou: deps.verrou ?? verrouNavigateur(),
        diffusion,
        horloge,
        // Hors session connectée, ou tant que le choix « autre compte » attend, rien ne part.
        utilisateurCourant: () =>
          peutEnvoyerRef.current ? (deps.utilisateurCourant ?? api.utilisateurDuJeton)() : null,
        reseauProbable: deps.reseauProbable ?? reseauProbable,
        planifier: deps.planifier,
        aleatoire: deps.aleatoire,
      });
      await cree.arreter(0);
      await s.purgerExpires(horloge()).catch((erreur: unknown) => {
        console.error(erreur);
        return 0;
      });
      if (!vivant) return;
      if (comptesDesactives.current.size > 0) {
        for (const compte of comptesDesactives.current) {
          await s.purgerUtilisateur(compte).catch((erreur: unknown) => console.error(erreur));
        }
        comptesDesactives.current.clear();
        maj({ compteDesactive: true });
        if (!vivant) return;
      }
      stockageRef.current = s;
      moteurRef.current = cree;
      setStockage(s);
      setMoteur(cree);
      await rafraichirRef.current();
    });
    return () => {
      vivant = false;
      void cree?.arreter(0);
      stockageRef.current = null;
      moteurRef.current = null;
      if (ouvert && !deps.stockage) ouvert.fermer();
    };
  }, [deps, diffusion, horloge, maj]);

  // Changement de compte ou déconnexion : la dernière lecture du panneau quitte la mémoire.
  const uidPrecedent = useRef<string | null>(null);
  useEffect(() => {
    if (uid !== null) {
      if (uidPrecedent.current !== null && uidPrecedent.current !== uid) memoire.vider();
      uidPrecedent.current = uid;
    }
    void rafraichir();
  }, [uid, rafraichir]);
  useEffect(() => {
    if (auth.etat === 'deconnecte') memoire.vider();
  }, [auth.etat]);

  // Le moteur tourne sous session connectée, une fois le choix « autre compte » fait.
  useEffect(() => {
    if (!moteur) return;
    if (peutEnvoyer) {
      moteur.reprendre();
      moteur.declencher('connexion');
    } else {
      void moteur.arreter();
    }
  }, [moteur, peutEnvoyer, uid]);

  // Événements du moteur : bandeau, ligne d'état, abonnés de l'interface.
  useEffect(() => {
    if (!moteur) return;
    const retirerEnCours = (contactId: string) => (i: InstantaneFile) =>
      i.envoiEnCours?.contactId === contactId ? null : i.envoiEnCours;
    return moteur.abonner((evenement) => {
      switch (evenement.type) {
        case 'envoi-debut':
          maj({ envoiEnCours: { contactId: evenement.contactId, rang: evenement.rang, total: evenement.total } });
          break;
        case 'envoye':
          maj((i) => ({
            envoiEnCours: retirerEnCours(evenement.contactId)(i),
            echecsConsecutifs: 0,
            enEchecDepuis: null,
            dernierePanne: null,
            dernierEnvoiLe: horloge(),
          }));
          void rafraichir();
          break;
        case 'refuse':
          maj((i) => ({ envoiEnCours: retirerEnCours(evenement.contactId)(i) }));
          void rafraichir();
          break;
        case 'panne':
          maj((i) => ({
            envoiEnCours: retirerEnCours(evenement.contactId)(i),
            echecsConsecutifs: i.echecsConsecutifs + 1,
            enEchecDepuis: i.enEchecDepuis ?? horloge(),
            dernierePanne: evenement.message,
            prochainEssai: evenement.prochaineTentativeLe,
            enLigne: lireReseau(),
          }));
          void rafraichir();
          break;
        case 'tournee-fin': {
          const { bilan } = evenement;
          maj({ envoiEnCours: null, prochainEssai: bilan.prochainEssai, enLigne: lireReseau() });
          void rafraichir().then((enAttente) => {
            if (enAttente !== 0) return;
            maj({ echecsConsecutifs: 0, enEchecDepuis: null, dernierePanne: null });
            if (bilan.envoyes.length === 0) return;
            if (minuteurToutEnvoye.current) clearTimeout(minuteurToutEnvoye.current);
            maj({ toutEnvoyeLe: horloge() });
            minuteurToutEnvoye.current = setTimeout(() => maj({ toutEnvoyeLe: null }), TOUT_ENVOYE_MS);
          });
          break;
        }
        case 'file-modifiee':
          void rafraichir();
          break;
        default:
          break;
      }
      for (const ecouteur of [...ecouteurs.current]) {
        try {
          ecouteur(evenement);
        } catch (erreur) {
          console.error(erreur);
        }
      }
    });
  }, [moteur, horloge, maj, rafraichir]);

  // Déclencheurs : autres onglets, réseau, session, retour à l'écran, purge horaire, mise à jour.
  useEffect(() => {
    const declencher = (raison: Parameters<MoteurEnvois['declencher']>[0]) => moteurRef.current?.declencher(raison);
    const sonder = deps.sonder ?? (() => api.appel('/sante', { delaiMs: DELAI_SONDE_MS }));
    const nettoyages = [
      diffusion.ecouter((message) => {
        if (message.type === 'file-modifiee') {
          void rafraichir();
          declencher('autre-onglet');
        } else if (message.type === 'envoi-termine') {
          void rafraichir();
        } else if (message.type === 'mise-a-jour-requise') {
          maj({ miseAJourRequise: true });
        }
      }),
      api.ecouterReseau((reseau) => {
        maj({ enLigne: lireReseau() });
        if (reseau === 'en_ligne') declencher('en-ligne');
      }),
      api.ecouterSession((evenement) => {
        if (evenement.type === 'renouvelee') {
          declencher('session-reprise');
        } else if (evenement.type === 'perdue' && evenement.code === 'COMPTE_INACTIF' && evenement.utilisateurId) {
          // Compte désactivé (téléphone perdu, départ) : ses saisies quittent le téléphone.
          // Au démarrage, la réponse peut précéder l'ouverture du stockage : la purge attend.
          memoire.vider();
          const s = stockageRef.current;
          if (!s) {
            comptesDesactives.current.add(evenement.utilisateurId);
          } else {
            void s
              .purgerUtilisateur(evenement.utilisateurId)
              .catch((erreur: unknown) => console.error(erreur))
              .finally(() => {
                maj({ compteDesactive: true });
                void rafraichir();
              });
          }
        }
        // SESSION_REVOQUEE, SESSION_EXPIREE : rien n'est effacé, les saisies attendent la reconnexion.
      }),
      ecouterEvenement(globalThis.window, 'online', () => {
        maj({ enLigne: lireReseau() });
        void Promise.resolve()
          .then(sonder)
          .catch(() => undefined)
          .finally(() => maj({ enLigne: lireReseau() }));
        declencher('en-ligne');
      }),
      ecouterEvenement(globalThis.window, 'offline', () => maj({ enLigne: false })),
      ecouterEvenement(globalThis.document, 'visibilitychange', () => {
        if (document.visibilityState === 'visible') declencher('visible');
      }),
      ecouterEvenement(globalThis.window, 'pageshow', () => {
        void rafraichir();
        declencher('visible');
      }),
      ecouterEvenement(globalThis.window, EVENEMENT_MISE_A_JOUR, () => maj({ miseAJourRequise: true })),
    ];
    const purge = setInterval(() => {
      void stockageRef.current
        ?.purgerExpires(horloge())
        .then((nombre) => {
          if (nombre > 0) void rafraichir();
        })
        .catch((erreur: unknown) => console.error(erreur));
    }, PURGE_PERIODE_MS);
    return () => {
      for (const nettoyer of nettoyages) nettoyer();
      clearInterval(purge);
    };
  }, [deps, diffusion, horloge, maj, rafraichir]);

  useEffect(
    () => () => {
      if (minuteurToutEnvoye.current) clearTimeout(minuteurToutEnvoye.current);
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Gestes de l'interface
  // -------------------------------------------------------------------------

  const cle = useCallback((contactId: string): CleSaisie | null => {
    const compte = uidRef.current;
    return compte ? [compte, contactId] : null;
  }, []);

  const mettreEnFile = useCallback(
    async (demande: DemandeMiseEnFile): Promise<ResultatMiseEnFile> => {
      const m = moteurRef.current;
      const s = stockageRef.current;
      if (m) return m.mettreEnFile(demande);
      if (!s) return { ok: false, raison: 'STOCKAGE_INDISPONIBLE', message: MESSAGE_STOCKAGE_INDISPONIBLE };
      const resultat = await s.mettreEnFile(demande, horloge());
      void rafraichir();
      return resultat;
    },
    [horloge, rafraichir],
  );

  const file = useMemo<ApiFile>(
    () => ({
      instantane: { ...etat, autresComptes },
      stockage,
      diffusion,
      mettreEnFile,
      attendreContact: (contactId, delaiMs) =>
        moteurRef.current ? moteurRef.current.attendreContact(contactId, delaiMs) : Promise.resolve('en_attente'),
      lire: async (contactId) => {
        const c = cle(contactId);
        return c && stockageRef.current ? stockageRef.current.lire(c) : undefined;
      },
      envoyerMaintenant: () => void moteurRef.current?.reessayerMaintenant(),
      envoyerEvaluationSeule: async (contactId) => {
        const c = cle(contactId);
        if (c && moteurRef.current) await moteurRef.current.envoyerEvaluationSeule(c);
      },
      abandonner: async (contactId) => {
        const c = cle(contactId);
        if (!c) return;
        if (moteurRef.current) await moteurRef.current.abandonner(c);
        else await stockageRef.current?.abandonner(c);
        await rafraichir();
      },
      ecouter: (ecouteur) => {
        ecouteurs.current.add(ecouteur);
        return () => {
          ecouteurs.current.delete(ecouteur);
        };
      },
      effacerAutresComptes: async () => {
        const compte = uidRef.current;
        if (!compte) return;
        await stockageRef.current?.purgerAutresQue(compte).catch((erreur: unknown) => console.error(erreur));
        setAutreCompteChoisi(compte);
        await rafraichir();
      },
      oublierPurgeAnciennete: () => maj({ effaceesPourAnciennete: 0 }),
      oublierCompteDesactive: () => maj({ compteDesactive: false }),
    }),
    [etat, autresComptes, stockage, diffusion, mettreEnFile, cle, rafraichir, maj],
  );

  // -------------------------------------------------------------------------
  // Déconnexion volontaire : les saisies du compte partent du téléphone, après confirmation
  // -------------------------------------------------------------------------

  const { deconnecter } = auth;
  const utilisateurId = auth.utilisateur?.id ?? null;

  const compterSaisies = useCallback(
    async (compte: string): Promise<{ nombre: number; refusees: number }> => {
      const s = stockageRef.current;
      if (!s) return { nombre: 0, refusees: 0 };
      const recensement = await s.recenser(horloge());
      const resumes = await s.resumes(compte);
      return {
        nombre: recensement.parUtilisateur[compte] ?? 0,
        refusees: resumes.filter((r) => r.etat === 'refuse' && !r.refus?.contenuEfface).length,
      };
    },
    [horloge],
  );

  const fermerEtPurger = useCallback(
    async (compte: string | null): Promise<void> => {
      setDialogue(null);
      await moteurRef.current?.arreter(ARRET_DECONNEXION_MS);
      // La marque de déconnexion est posée dès cet appel : plus aucun brouillon ne s'écrit.
      const fin = deconnecter();
      memoire.vider();
      const purger = async () => {
        if (!compte || !stockageRef.current) return;
        await stockageRef.current.purgerUtilisateur(compte).catch((erreur: unknown) => console.error(erreur));
      };
      await purger();
      await fin.catch(() => undefined);
      // Seconde passe : écritures tardives d'un autre onglet.
      await purger();
      await rafraichir();
    },
    [deconnecter, rafraichir],
  );

  const deconnexion = useMemo<ApiDeconnexion>(
    () => ({
      dialogue,
      demander: async () => {
        if (!utilisateurId) {
          await deconnecter();
          return;
        }
        const compte = await compterSaisies(utilisateurId).catch(() => ({ nombre: 0, refusees: 0 }));
        if (compte.nombre === 0) await fermerEtPurger(utilisateurId);
        else setDialogue({ ...compte, enLigne: lireReseau(), apresEnvoiPartiel: false });
      },
      confirmer: () => fermerEtPurger(utilisateurId),
      envoyerPuisDeconnecter: async () => {
        if (!utilisateurId) return;
        await moteurRef.current?.reessayerMaintenant();
        const compte = await compterSaisies(utilisateurId).catch(() => null);
        if (compte && compte.nombre === 0) await fermerEtPurger(utilisateurId);
        else if (compte) setDialogue({ ...compte, enLigne: lireReseau(), apresEnvoiPartiel: true });
      },
      annuler: () => setDialogue(null),
    }),
    [dialogue, utilisateurId, deconnecter, compterSaisies, fermerEtPurger],
  );

  const valeur = useMemo<ValeurFile>(() => ({ file, deconnexion }), [file, deconnexion]);
  return <ContexteFile.Provider value={valeur}>{enfants}</ContexteFile.Provider>;
}
